/**
 * The kept cloud project or account of a data plane, as the Data planes page shows it (plan
 * 2026-10-22-retained-tenant-projects, D-18).
 *
 * A destroy of a tenant data plane keeps its GCP project or its AWS account. The control plane
 * keeps a record of it, and `GET /v1/admin/data-planes` puts the record on the row as
 * `retainedProject` (D-17). This module turns that field into the words the row prints. The retention
 * verdict is the server's `retentionEnded`, and the module prints the dates as facts.
 *
 * A removal of a cross-cloud satellite keeps the satellite's own project or account too. The list
 * row carries each such record in `retainedSatelliteProjects`, with its region (R-010 and R-035 of
 * plan 2026-10-24-aws-dp-identity-and-lifecycle), and `retainedSatelliteProjectView` gives its words.
 *
 * The term is "retained project" for GCP and "retained account" for AWS (D-5). The door-pull loop uses
 * "retired" for a destroying or destroyed data plane, so this page keeps the word "retained".
 */
import type { DataPlaneSummary, RetainedRecordFacts, RetainedSatelliteProject } from '../api/dataPlanes.api';
import { satelliteRegionFits } from '../api/dpSatellites.api';
import { maskCredentialShapes } from '../platform/credential-mask';
import { REDACTED_TEXT, displayUntrustedName } from '../platform/untrusted-text';

/**
 * The value that site-admin-api writes into `instanceType` and `versionStatus` of a row it made from a
 * retained record (`RETAINED_ROW_UNKNOWN` of `dp-retained-project.ts`). A placement row carries
 * `shared` or `dedicated` there.
 */
export const RETAINED_ROW_UNKNOWN = 'unknown';

/**
 * Server text of a retained record as the row shows it: the credential shapes of a name masked
 * (`maskCredentialShapes` with `letterRuns: 'random'`), then one line with every invisible code point
 * escaped (`displayUntrustedName`). An id, a project number, an account name and a data plane name pass it
 * (review rows WS-14-P3-R51 and R52). Pure.
 */
export function retainedRecordText(text: string): string {
  return displayUntrustedName(maskCredentialShapes(text, { letterRuns: 'random' }));
}

/**
 * The runbook that holds the read and the hand-delete commands for a retained project or account
 * (D-12, D-18). WS-5 of the plan writes it. The row links it as this fixed URL, so the `href` of the
 * link is this constant.
 */
export const RETAINED_PROJECTS_RUNBOOK_URL =
  'https://github.com/witness-ai/zion-infra/blob/main/requirements/architecture/runbooks/retained-tenant-projects.md';

/**
 * The retention facts of a `destroyed` record. The two dates are ISO 8601 as the server sent them,
 * or `null` when the record holds none. `ended` is the server's verdict and nothing else.
 */
export interface RetentionView {
  readonly destroyedAt: string | null;
  readonly endsAt: string | null;
  readonly ended: boolean;
}

/** The words of one retained project or account, for the row of the Data planes page. */
export interface RetainedProjectView {
  /** `retained project` for GCP, `retained account` for AWS. */
  readonly noun: 'retained project' | 'retained account';
  /** The GCP project id or the AWS account id, or `null` when the record holds none. */
  readonly id: string | null;
  /**
   * The second fact of the id, for its tooltip:
   * - `project number <n>` for GCP;
   * - `account name <n>` for AWS;
   * - `null` when the record holds none.
   */
  readonly idDetail: string | null;
  /** `destroyed`, or `onboarding failed` for a project or account a failed onboarding left. */
  readonly cause: 'destroyed' | 'onboarding failed';
  /** The dates and the verdict for a `destroyed` record; `null` for `onboarding_failed`. */
  readonly retention: RetentionView | null;
  /** The TenantCloudBase still exists, so a new onboarding of the same name refuses. */
  readonly nameBlocked: boolean;
}

/**
 * The view of one `retainedProject` field, or of the shared facts of one satellite record.
 *
 * Input: the field as the server sent it. The function reads no `placementMissing`, so a member of
 * `retainedSatelliteProjects` is also a valid input; `retainedSatelliteProjectView` passes one.
 * Output per class:
 *
 * - `cloud: 'gcp'`: the function reads the GCP fields alone.
 *   - noun: `retained project`;
 *   - id: `gcpProjectId`;
 *   - detail: `project number <gcpProjectNumber>`.
 * - `cloud: 'aws'`: the function reads the AWS fields alone.
 *   - noun: `retained account`;
 *   - id: `awsAccountId`;
 *   - detail: `account name <awsAccountName>`.
 * - A `null` id or a `null` second fact stays `null`; the caller renders an absence for the id. Any
 *   other id and second fact pass {@link retainedRecordText}, so a credential shape in them reads
 *   `[redacted]` in the text and in the `title` (review row WS-14-P3-R51).
 * - `cause: 'destroyed'`: `retention` carries the three retention fields unchanged:
 *   - `destroyedAt`;
 *   - `retentionEndsAt`;
 *   - `retentionEnded`.
 * - `cause: 'onboarding_failed'`: `retention` is `null`. The function skips the dates, because the
 *   server sends none for this cause.
 *
 * The function is pure and does not throw.
 */
export function retainedProjectView(project: RetainedRecordFacts): RetainedProjectView {
  const gcp = project.cloud === 'gcp';
  const id = gcp ? project.gcpProjectId : project.awsAccountId;
  const second = gcp ? project.gcpProjectNumber : project.awsAccountName;
  return {
    noun: gcp ? 'retained project' : 'retained account',
    id: id === null ? null : retainedRecordText(id),
    idDetail: second === null ? null : `${gcp ? 'project number' : 'account name'} ${retainedRecordText(second)}`,
    cause: project.cause === 'destroyed' ? 'destroyed' : 'onboarding failed',
    retention:
      project.cause === 'destroyed'
        ? { destroyedAt: project.destroyedAt, endsAt: project.retentionEndsAt, ended: project.retentionEnded }
        : null,
    nameBlocked: project.nameBlocked,
  };
}

/** The words of one retained satellite project or account, for the row of the Data planes page. */
export interface RetainedSatelliteProjectView extends Omit<RetainedProjectView, 'noun'> {
  /** `retained satellite project` for GCP, `retained satellite account` for AWS. */
  readonly noun: 'retained satellite project' | 'retained satellite account';
  /** The satellite region as the server sent it. */
  readonly region: string;
}

/**
 * The view of one member of `retainedSatelliteProjects` (R-035 of plan
 * 2026-10-24-aws-dp-identity-and-lifecycle).
 *
 * Input: one satellite record as the server sent it. Output:
 * - noun: `retained satellite project` for `cloud: 'gcp'`, `retained satellite account` for
 *   `cloud: 'aws'`. The satellite cloud decides, and the home cloud does not;
 * - region: `region` when it fits the region form of `cloud`, else `[redacted]`
 *   ({@link retainedSatelliteRegionText});
 * - id, idDetail, cause, retention and nameBlocked: the members of {@link retainedProjectView} for the
 *   same facts, with the same rules per class.
 *
 * The function is pure and does not throw.
 */
export function retainedSatelliteProjectView(record: RetainedSatelliteProject): RetainedSatelliteProjectView {
  const shared = retainedProjectView(record);
  return {
    ...shared,
    noun: record.cloud === 'gcp' ? 'retained satellite project' : 'retained satellite account',
    region: retainedSatelliteRegionText(record),
  };
}

/**
 * The region of a satellite record, made safe for the note and the `name blocked` sentence: `region` when
 * it fits the region form of the record's own cloud (`satelliteRegionFits`), else `REDACTED_TEXT`. The
 * fleet read carries no runtime schema, so the region is server text that no check has read; a region
 * form is built from closed word lists and digits, so a credential in the field cannot reach the screen
 * (review row WS-14-P2-R38). A record whose cloud is neither `gcp` nor `aws` gives `REDACTED_TEXT`. Pure.
 */
export function retainedSatelliteRegionText(record: Pick<RetainedSatelliteProject, 'cloud' | 'region'>): string {
  const cloud: string = record.cloud;
  return (cloud === 'gcp' || cloud === 'aws') && satelliteRegionFits(cloud, record.region) ? record.region : REDACTED_TEXT;
}

/**
 * The sentence behind the `name blocked` badge of a satellite record, for its tooltip and for a screen
 * reader. `name` is the data plane name and `region` the satellite region (as
 * `retainedSatelliteRegionText` gives it); React renders the sentence as text. A new build of a
 * cross-cloud satellite in the same region names the same TenantCloudBase (`satelliteTenantCloudBaseName`
 * of provisioning-svc).
 *
 * The name passes {@link retainedRecordText}. When that changes it (a credential shape, an invisible code
 * point), or the region is `[redacted]`, the sentence names no TenantCloudBase, because the text on screen
 * would not be the real name (review row WS-14-P3-R52).
 */
export function satelliteNameBlockedReason(name: string, region: string): string {
  const shown = retainedRecordText(name);
  if (shown !== name || region === REDACTED_TEXT) {
    return 'The TenantCloudBase of this satellite still exists. A new satellite of this data plane in the same region names the same TenantCloudBase until a person deletes it. The runbook holds the command.';
  }
  return `The TenantCloudBase tcb-${name}-sat-${region} still exists. A new satellite of ${name} in ${region} names the same TenantCloudBase until a person deletes it. The runbook holds the command.`;
}

/**
 * The sentence behind the `name blocked` badge, for its tooltip and for a screen reader. `name` is
 * the data plane name; React renders the sentence as text. The name passes {@link retainedRecordText};
 * when that changes it, the sentence names no TenantCloudBase (review row WS-14-P3-R52).
 */
export function nameBlockedReason(name: string): string {
  if (retainedRecordText(name) !== name) {
    return 'The TenantCloudBase of this data plane still exists, so a new onboarding of the same name is refused until a person deletes it. The runbook holds the command.';
  }
  return `The TenantCloudBase tcb-${name} still exists, so a new onboarding of the name ${name} is refused until a person deletes it. The runbook holds the command.`;
}

/**
 * Whether a list row stands for a placement that exists (R-012 of plan
 * 2026-10-22-retained-tenant-projects).
 *
 * The server makes a row from a retained-project record alone when no placement row exists, and
 * sets `retainedProject.placementMissing` on it. It also makes a row from the satellite records of a
 * data plane that has no placement row and no home record, with no `retainedProject` (rule 5 of
 * `DpAdminService.list()`, review row WS-13-P2-R2); such a row carries {@link RETAINED_ROW_UNKNOWN} as
 * its `instanceType` (review row WS-14-P3-R7). The Data planes page shows the name of such a row as
 * plain text and reads no live status for it. The data plane pickers leave the row out, so each picker
 * offers only data planes that exist.
 *
 * Input: one row as the server sent it. Output:
 * - `false` when `retainedProject.placementMissing` is `true`;
 * - `false` for a row with no `retainedProject`, a non-empty `retainedSatelliteProjects` and the
 *   `instanceType` {@link RETAINED_ROW_UNKNOWN};
 * - `true` for any other row with no `retainedProject`;
 * - `true` for a row whose record has a placement.
 * The function is pure and does not throw.
 */
export function hasPlacement(dp: DataPlaneSummary): boolean {
  if (dp.retainedProject !== undefined) return !dp.retainedProject.placementMissing;
  return !(dp.instanceType === RETAINED_ROW_UNKNOWN && retainedSatelliteRecords([dp]).length > 0);
}

/**
 * The retained satellite records of `dps` that are a list, flattened; a row whose
 * `retainedSatelliteProjects` is absent or not a list gives none. The list read of the fleet carries no
 * runtime schema, so the shape is checked here before a `.map()` (review row WS-14-R5). Pure.
 */
export function retainedSatelliteRecords(dps: readonly DataPlaneSummary[]): readonly RetainedSatelliteProject[] {
  return dps.flatMap((dp) => {
    const records: unknown = dp.retainedSatelliteProjects;
    return Array.isArray(records) ? (records as readonly RetainedSatelliteProject[]) : [];
  });
}

/** The sentence of the live region when a read finds no retained satellite record after one that found some. */
export const RETAINED_SATELLITE_NONE_SENTENCE = 'The loaded data planes show no retained satellite record.';

/**
 * The sentence of the Data planes page's polite live region for the retained satellite records of the
 * loaded rows, `dps` (review row WS-14-R5): the count of records with their regions, and the count
 * whose satellite name is blocked with their regions, each region as `retainedSatelliteRegionText` gives
 * it, in the server order. `''` when no loaded row carries one. The page passes every row of the fleet
 * read, before the state chips and the text filters narrow it, so a filter changes nothing here (review
 * row WS-14-P2-R34). A read that adds or removes a record, or that blocks or frees a name, changes the
 * regions, so the sentence changes and is announced, also when the counts stay (review row
 * WS-14-P3-R42). The region sits outside the table, because the table holds no live region (the fan-out
 * rule of the page). Pure.
 */
export function retainedSatelliteAnnouncement(dps: readonly DataPlaneSummary[]): string {
  const records = retainedSatelliteRecords(dps);
  if (records.length === 0) return '';
  const blocked = records.filter((record) => record.nameBlocked);
  const regions = (list: readonly RetainedSatelliteProject[]): string => list.map((record) => retainedSatelliteRegionText(record)).join(', ');
  const noun = records.length === 1 ? 'retained satellite record' : 'retained satellite records';
  const names = blocked.length === 0 ? '' : `, in ${regions(blocked)}`;
  return `The loaded data planes show ${records.length} ${noun}, in ${regions(records)}; ${blocked.length} of them ${blocked.length === 1 ? 'blocks' : 'block'} a satellite name${names}.`;
}
